import { FunctionComponent, useEffect, useState } from "react";
import { format } from "date-fns";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useAuth } from "../../context/AuthContext";
import SkeletonLoader from "../common/SkeletonLoader";
import { TODOS } from "../../api/query";
import { useNavigate } from "react-router-dom";
import { TODO_ACTIONS } from "../../api/mutation";

export type ActionRowType = {
    imageurl?: string;
    heading?: string;
};

const MyTodos: FunctionComponent<ActionRowType> = ({
    heading,
}) => {
    const navigate = useNavigate()
    const { userInfo } = useAuth()
    const employeeID = userInfo?.employee_id || '';

    const [currentPage, setCurrentPage] = useState(0); // pagination state
    const itemsPerPage = 3; // 3 tasks per page

    const now = new Date();
    const startDate = format(now, "yyyy-MM-dd");
    const currentDateAndTime = format(now, "yyyy-MM-dd'T'HH:mm:ssxxx").replace(/(\+|\-)\d{2}:\d{2}$/, "Z");

    //api
    const [getToDos, { loading, data }] = useLazyQuery(
        TODOS,
        {
            fetchPolicy: "no-cache"
        }
    );

    const [updateTodoStatus, { loading: updateToDoLoading }] = useMutation(TODO_ACTIONS, {
        refetchQueries: [TODOS], // optional: refresh list after update
        awaitRefetchQueries: true
    });

    useEffect(() => {
        getToDos({
            variables: {
                member_id: parseInt(employeeID),
                date: startDate
            }
        })
    }, [employeeID])

    if (loading) {
        return (
            <div
                className="self-stretch shadow-[0px_1px_4px_rgba(0,_0,_0,_0.25)] mobile-action-card-blg-dv rounded-lg bg-surface-bg-primary flex flex-col items-start justify-start p-4 box-border gap-[6px] max-w-full text-left text-base text-colors-text-black font-body-xs-semi-bold "
            >
                <div className="self-stretch flex flex-row items-center justify-start gap-[16px] max-w-full mq950:flex-wrap inner-mobile-action-card-blg-dv">
                    <div className="flex-1 flex flex-row items-start justify-start gap-[6px] max-w-full">
                        <h5 className="relative font-bold text-xl-dv my-0 capitalize dark:text-white">{heading}</h5>
                    </div>
                </div>
                <div className="w-full flex flex-col gap-4">
                    <div className="flex justify-between items-center">
                        <SkeletonLoader count={1} height="h-4" width="w-2/5" />
                        <SkeletonLoader count={1} height="h-6" width="w-16" borderRadius="rounded-full" />
                    </div>
                    <div className="flex gap-4 items-center">
                        <SkeletonLoader count={1} height="h-3" width="w-20" />
                        <SkeletonLoader count={1} height="h-3" width="w-24" />
                    </div>
                    <div className="flex justify-between items-center">
                        <SkeletonLoader count={1} height="h-4" width="w-1/3" />
                        <SkeletonLoader count={1} height="h-6" width="w-16" borderRadius="rounded-full" />
                    </div>
                    <div className="flex gap-4 items-center">
                        <SkeletonLoader count={1} height="h-3" width="w-28" />
                        <SkeletonLoader count={1} height="h-3" width="w-32" />
                    </div>
                    <div className="flex justify-between items-center">
                        <SkeletonLoader count={1} height="h-4" width="w-2/5" />
                        <SkeletonLoader count={1} height="h-6" width="w-16" borderRadius="rounded-full" />
                    </div>
                    <div className="flex gap-4 items-center">
                        <SkeletonLoader count={1} height="h-3" width="w-24" />
                        <SkeletonLoader count={1} height="h-3" width="w-20" />
                    </div>
                </div>
            </div>
        )
    }

    const todos = data?.todos?.todos || [];
    const sortedTodos = todos
        .slice() // make a shallow copy to avoid mutating original data
        .sort((a: any, b: any) => new Date(a.end_datetime).getTime() - new Date(b.end_datetime).getTime()); // ascending order

    // Pagination calculations
    const totalPages = Math.ceil(sortedTodos.length / itemsPerPage);
    const startIndex = currentPage * itemsPerPage;
    const visibleTodos = sortedTodos.slice(startIndex, startIndex + itemsPerPage);

    return (
        <>
            <div
                className="self-stretch shadow-[0px_1px_4px_rgba(0,_0,_0,_0.25)] mobile-action-card-blg-dv rounded-lg bg-surface-bg-primary flex flex-col items-start justify-start p-4 box-border gap-[6px] max-w-full text-left text-base text-colors-text-black font-body-xs-semi-bold "
            >
                <div className="self-stretch flex flex-row items-center justify-start gap-[16px] max-w-full mq950:flex-wrap inner-mobile-action-card-blg-dv">
                    <div className="flex-1 flex flex-row items-start justify-start gap-[6px] max-w-full">
                        <h5 className="relative font-bold text-xl-dv my-0 capitalize dark:text-white">{`${heading} (${data?.todos?.todos?.length ?? 0})`}</h5>
                    </div>
                        <div className="flex items-center gap-2 cursor-pointer"  onClick={() => navigate('/all_todo')}>
                    <span className="text-sm text-gray-600 dark:text-gray-300">View To Dos</span>
                    <svg className="w-5 h-5 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </div>
                </div>
                <div className={`max-w-full flex min-w-full max-h-[560px] overflow-x-hidden overflow-y-auto ${data?.todos?.todos?.length > 6 ? 'shiftwith-exception-scroll' : ''}`}>
                    <div className="grid grid-cols-3 mq1900:grid-cols-3 mq1425:grid-cols-2 mq1275:grid-cols-2 mq950:grid-cols-2 mq825:grid-cols-1 mq575:grid-cols-1 min-w-full  gap-[24px]">
                        {visibleTodos?.map((t: any) => {
                            return (
                                <div className="customtask_card_ui_dv  w-full relative rounded cursor-pointer bg-white border-whitesmoke border-solid border-[1px] box-border  flex items-start justify-start pt-4 px-4 pb-4 text-left text-base text-gray font-manrope"
                                    key={t?.id}
                                >
                                    <div className=" w-full relative rounded cursor-pointer   shrink-0 block flex-col items-start justify-start text-left text-base text-gray font-manrope">
                                        <div className="self-stretch flex flex-col items-start justify-start">
                                            <div className="self-stretch flex flex-col items-start justify-center gap-2 text-dimgray">
                                                <div className="self-stretch flex flex-row items-start justify-start gap-2">
                                                    <p className="w-[120px] relative inline-block shrink-0 dark:text-white my-0">Due Date:</p>
                                                    <p className="relative font-semibold text-gray dark:text-white my-0">
                                                        {format(t?.due_date, 'MM-dd-yyyy')}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="grid gap-2 w-full">
                                            <hr className="bdr-custom-taskcrad" />
                                            <div className="self-stretch flex flex-row items-center justify-start pb-2">
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <span className="relative font-semibold text-sm my-0 capitalize tasktitle_card">
                                                        {t?.title}
                                                    </span>
                                                </label>
                                            </div>
                                                  <div className="self-stretch flex flex-row items-center justify-start pb-2">
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                   

                                                    <p className="w-[120px] relative inline-block shrink-0 dark:text-white my-0">Assigned By :</p>
                                                    <p className="relative font-semibold text-gray dark:text-white my-0">
                                                       {t?.created_by_name}
                                                    </p>
                                                    
                                                </label>
                                            </div>
                                            <hr className="bdr-custom-taskcrad" />
                                            <div className="self-stretch flex flex-row items-start justify-between gap-0 mt-5">
                                                <div
                                                    onClick={() => {
                                                        navigate("/edit_todo", {
                                                            state: {
                                                                id: t?.id
                                                            }
                                                        })
                                                    }}
                                                    className="rounded-[100px] viewbg-dv right_status_clockout flex flex-row items-center justify-center py-1 px-2 text-sm text-white">
                                                    <div className="relative font-semibold">View</div>
                                                </div>
                                                {
                                                    t?.is_completed ?
   <div
                                                
                                                    className="rounded-[100px] flex flex-row items-center justify-center py-1 px-2 text-sm text-white bg-green-600">
                                                    <div className="relative font-semibold">Resolved</div>
                                                </div>
                                                :
                                                   <div
                                                    onClick={() => {
                                                        updateTodoStatus({
                                                                variables: {
                                                                    id: parseInt(t?.id),
                                                                    is_completed: true,
                                                                    completed_date: currentDateAndTime
                                                                }
                                                            })
                                                    }}
                                                    className="rounded-[100px] viewbg-dv right_status_clockout flex flex-row items-center justify-center py-1 px-2 text-sm text-white">
                                                    <div className="relative font-semibold">Resolve</div>
                                                </div>

                                                }
                                                 
                                            </div>
                                            
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                        {visibleTodos?.length === 0 && (
                            <>No data Available</>
                        )}
                        {
                            data?.todos?.status === "Failure" && (
                                <div className="border-[1px] border-solid border-surface-border min-w-full bg-white flex justify-center items-center action-nodeta_card">
                                    <span className="text-left text-sm text-text-text-secondary"> No data available</span>
                                </div>
                            )
                        }
                    </div>
                </div>
                {/* Pagination dots */}
                {totalPages > 1 && (
                    <div className="flex justify-center items-center mt-4 gap-2 pagination-action-blg">
                        {Array.from({ length: totalPages }).map((_, index) => (
                            <button
                                key={index}
                                onClick={() => setCurrentPage(index)}
                                className={`h-3 w-3 rounded-full transition-all ${index === currentPage ? " scale-110 active-pagination" : "bg-gray-300 hover:bg-gray-400 unactive-pagination"
                                    }`}
                            />
                        ))}
                    </div>
                )}
            </div >
            {updateToDoLoading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
                </div>
            )}
        </>
    );
};

export default MyTodos;
